import React from 'react';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useLanguage } from '../../../hooks/useLanguage';

const paymentMethods = [
  {
    name: "Nequi",
    color: "#E6007E",
    logo: "https://static.readdy.ai/image/18682db60553df15ba50b947d4252b50/9bbb0ea2fd6a8b625dfa0d1af5ad1fa4.png",
  },
  {
    name: "Daviplata",
    color: "#E30613",
    logo: "https://static.readdy.ai/image/18682db60553df15ba50b947d4252b50/4738bf97cbdee510c76b19003d71dd2e.png",
  },
  {
    name: "Bancolombia",
    color: "#FFDD00",
    logo: "https://static.readdy.ai/image/18682db60553df15ba50b947d4252b50/ac30a729568787a58fc2795433e34521.png",
  },
  {
    name: "Efecty",
    color: "#FFD700",
    logo: "https://static.readdy.ai/image/18682db60553df15ba50b947d4252b50/056c5be3003cf61f3b6a652d0e578a82.png",
  },
  {
    name: "Tarjeta",
    color: "#1A1F71",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Visa_Inc._logo.svg/200px-Visa_Inc._logo.svg.png",
    icon: "ri-bank-card-line",
  },
  {
    name: "PayPal",
    color: "#003087",
    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/b/b5/PayPal.svg/200px-PayPal.svg.png",
  },
];

export default function NequiBanner() {
  const [visible, setVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const { t } = useLanguage();

  // Show the banner after a short delay
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 300);
    return () => clearTimeout(timer);
  }, []);

  // Cycle through payment methods
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % paymentMethods.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  // Guard against an empty paymentMethods array
  if (!paymentMethods?.length) {
    return null;
  }

  return (
    <section
      className={`w-full transition-all duration-700 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
    >
      <div className="bg-gradient-to-r from-[#1a1a2e] via-[#16213e] to-[#0f3460] relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-32 h-32 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-40 h-40 bg-white/5 rounded-full translate-x-1/3 translate-y-1/3"></div>

        <div className="relative container mx-auto px-4 py-3.5 sm:py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-6">
            {/* Left: Text + Flag */}
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-white/10 rounded-xl flex items-center justify-center">
                <span className="text-xl sm:text-2xl">🇨🇴</span>
              </div>
              <div className="text-white text-center sm:text-left">
                <h3 className="text-sm sm:text-base font-bold">
                  {t('nequi.payment_methods')}
                </h3>
                <p className="text-white/70 text-[11px] sm:text-xs mt-0.5">
                  {t('nequi.subtitle')}
                </p>
              </div>
            </div>

            {/* Center: Payment method logos */}
            <div className="flex items-center gap-2 sm:gap-3">
              {paymentMethods.map((method, idx) => (
                <div
                  key={method.name}
                  className={`flex items-center gap-2 px-3 py-2 sm:px-4 sm:py-2.5 rounded-xl transition-all duration-500 cursor-default ${
                    activeIndex === idx
                      ? 'bg-white/15 scale-105 shadow-lg shadow-white/5'
                      : 'bg-white/5 scale-100'
                  }`}
                >
                  <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-white flex items-center justify-center flex-shrink-0">
                    {(method as any).icon ? (
                      <i className={`${(method as any).icon} text-[#1A1F71] text-sm sm:text-base`}></i>
                    ) : (
                      <img
                        src={method.logo}
                        alt={method.name}
                        className="w-4 h-4 sm:w-5 sm:h-5 object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    )}
                  </div>
                  <span
                    className={`text-[11px] sm:text-xs font-semibold transition-colors duration-500 whitespace-nowrap ${
                      activeIndex === idx ? 'text-white' : 'text-white/60'
                    }`}
                  >
                    {method.name === 'Tarjeta' ? t('nequi.card') : method.name}
                  </span>
                </div>
              ))}
            </div>

            {/* Right: CTA */}
            <Link
              to="/carrito"
              className="flex-shrink-0 inline-flex items-center gap-2 bg-gradient-to-r from-[#E6007E] to-[#C20060] text-white px-5 py-2.5 sm:px-6 sm:py-2.5 rounded-xl font-bold text-xs sm:text-sm hover:opacity-90 transition-all hover:scale-105 hover:shadow-lg cursor-pointer whitespace-nowrap"
            >
              <i className="ri-shopping-cart-2-line"></i>
              {t('nequi.buy_now')}
              <i className="ri-arrow-right-s-line"></i>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
